import { useMemo, useState } from 'react'

import type { PlanCounterpartyMismatchPartner } from '@ebalance/proto/bffuser/v1/submission_management_model_pb'

import type { MismatchNavBannerProps } from '~/pages/ProcurementPlanDetail/components/MismatchNavBanner'
import type { BreakdownColumn } from '~/pages/ProcurementPlanDetail/models/columns'
import {
  type MismatchTableState,
  buildMismatchItems,
  mismatchItemsSignature,
} from '~/pages/ProcurementPlanDetail/models/mismatchItems'

/** 注目なし (design の初期表示「— / N」) */
const NO_FOCUS = -1

const NO_PARTNERS: readonly PlanCounterpartyMismatchPartner[] = []

export type MismatchNavigation = {
  /** 不整合バナー (`MismatchNavBanner`) にそのまま渡す props */
  banner: MismatchNavBannerProps
  /** 計画テーブル (page の `PlanTable` の `mismatch`) にそのまま渡す状態 */
  table: MismatchTableState
  /** 閉じたバナーを開き直す (誘導メッセージの「不整合を確認」、AC-PLC-020-2) */
  reopen: () => void
}

/**
 * 不整合の箇所の組み立てと不整合バナーの状態 (AC-PPD-009〜017、issue #10317)。
 * バナーとテーブルは同じ件 (閉じている間はどちらも空) を受け取る。
 *
 * - 件は `GetPlanCounterpartyMismatch` の `partners[]` と表示中の列から組む
 *   (`buildMismatchItems`)。取得前・失敗・機能 OFF・解消済みは件が 0
 * - 初期は注目なし。次へ / 前へは末尾 ⇄ 先頭でループし、注目なしからの次へは先頭、前へは末尾
 * - 件の集合が変わったら (種別の切替・新しい通知・解消) 注目なしに戻す
 * - 閉じるは、その未解消の集合 (`resolutionToken`) と計画 (`planIdentity`) の組に対して効く。
 *   token が変わる (新しい通知・解消) か計画が変わると閉じた状態を解く。`reopen` でも解く
 */
export function useMismatchNavigation({
  mismatch,
  procurementColumns,
  salesColumns,
  planIdentity,
}: {
  /** `GetPlanCounterpartyMismatch` の応答。取得前・失敗は undefined */
  mismatch:
    | {
        partners: readonly PlanCounterpartyMismatchPartner[]
        resolutionToken: string
      }
    | undefined
  /** 表示中の列 (種別選択後、表示モード適用前) */
  procurementColumns: readonly BreakdownColumn[]
  salesColumns: readonly BreakdownColumn[]
  planIdentity: string
}): MismatchNavigation {
  const partners = mismatch?.partners ?? NO_PARTNERS
  const items = useMemo(
    () => buildMismatchItems({ partners, procurementColumns, salesColumns }),
    [partners, procurementColumns, salesColumns],
  )
  const dismissKey = `${planIdentity}\n${mismatch?.resolutionToken ?? ''}`
  const [dismissedKey, setDismissedKey] = useState<string | undefined>(
    undefined,
  )
  const [focusedIndex, setFocusedIndex] = useState(NO_FOCUS)
  const [navigationId, setNavigationId] = useState(0)

  // 件の集合が変わったら注目なしへ戻す (描画中に前回の値と比べて state を直す React の作法)
  const signature = mismatchItemsSignature(items)
  const [previousSignature, setPreviousSignature] = useState(signature)
  if (signature !== previousSignature) {
    setPreviousSignature(signature)
    setFocusedIndex(NO_FOCUS)
  }

  const visibleItems = dismissedKey === dismissKey ? [] : items
  const count = visibleItems.length
  // 閉じている間 (件が 0) は注目なし
  const currentIndex =
    focusedIndex !== NO_FOCUS && focusedIndex < count ? focusedIndex : undefined
  const focusedItem =
    currentIndex === undefined ? undefined : visibleItems[currentIndex]

  const move = (delta: 1 | -1): void => {
    if (count === 0) return
    const nextIndex =
      currentIndex === undefined
        ? delta === 1
          ? 0
          : count - 1
        : (currentIndex + delta + count) % count
    setFocusedIndex(nextIndex)
    setNavigationId((n) => n + 1)
  }

  return {
    banner: {
      items: visibleItems,
      focusedIndex: currentIndex,
      onNext: () => {
        move(1)
      },
      onPrev: () => {
        move(-1)
      },
      onClose: () => {
        setDismissedKey(dismissKey)
        setFocusedIndex(NO_FOCUS)
      },
    },
    reopen: () => {
      setDismissedKey(undefined)
    },
    table: {
      items: visibleItems,
      focus:
        focusedItem === undefined
          ? undefined
          : { item: focusedItem, navigationId },
    },
  }
}
